import type { MusicTrackId, SoundKind } from '../game/types';

type SynthSettings = readonly [number, number, OscillatorType, number];
type AudioWindow = Window & { webkitAudioContext?: typeof AudioContext };

export interface MusicTrackDefinition {
  id: MusicTrackId;
  title: string;
  artist: string;
  src: string | null;
  sourceUrl: string;
  license: string;
}

/** City is the retained CC0 track; the other slots are original procedural loops. */
export const MUSIC_TRACKS: Record<MusicTrackId, MusicTrackDefinition> = {
  city: {
    id: 'city',
    title: 'CITY LOOP',
    artist: 'wipics',
    src: '/audio/music/city-loop.mp3',
    sourceUrl: 'https://opengameart.org/content/city-loop-0',
    license: 'CC0',
  },
  electronic: {
    id: 'electronic',
    title: 'NEON FRONTLINE',
    artist: 'ARMADA AUDIO',
    src: null,
    sourceUrl: 'https://github.com/thecoooler/armadatanks',
    license: 'procedural',
  },
  synth: {
    id: 'synth',
    title: 'ORBITAL ASSAULT',
    artist: 'ARMADA SYNTH',
    src: null,
    sourceUrl: 'https://github.com/thecoooler/armadatanks',
    license: 'procedural',
  },
};

const SFX: Record<SoundKind, SynthSettings> = {
  shot: [420, 0.055, 'square', 0.06],
  flame: [170, 0.075, 'sawtooth', 0.045],
  rocket: [95, 0.17, 'sawtooth', 0.1],
  hit: [620, 0.045, 'triangle', 0.045],
  explode: [85, 0.22, 'sawtooth', 0.12],
  enemy: [125, 0.08, 'square', 0.035],
  enemyShot: [155, 0.12, 'square', 0.045],
  base: [70, 0.3, 'sine', 0.12],
  upgrade: [740, 0.18, 'triangle', 0.08],
  level: [330, 0.5, 'sine', 0.1],
  gameover: [55, 0.8, 'sawtooth', 0.13],
  deny: [90, 0.12, 'square', 0.06],
  pickup: [880, 0.14, 'triangle', 0.08],
  shield: [260, 0.3, 'sine', 0.08],
  damage: [110, 0.18, 'sawtooth', 0.1],
  ui: [680, 0.055, 'square', 0.045],
  engine: [65, 0.18, 'sawtooth', 0.045],
};

const SOUND_ASSETS: Partial<Record<SoundKind, readonly string[]>> = {
  shot: ['/audio/sfx/laserSmall_000.ogg', '/audio/sfx/laserSmall_001.ogg', '/audio/sfx/laserSmall_002.ogg'],
  flame: ['/audio/sfx/laserRetro_000.ogg', '/audio/sfx/laserRetro_001.ogg', '/audio/sfx/laserRetro_002.ogg'],
  rocket: ['/audio/sfx/laserLarge_000.ogg', '/audio/sfx/laserLarge_001.ogg', '/audio/sfx/laserLarge_002.ogg'],
  hit: ['/audio/sfx/impactMetal_000.ogg', '/audio/sfx/impactMetal_001.ogg', '/audio/sfx/impactMetal_002.ogg'],
  explode: ['/audio/sfx/explosionCrunch_000.ogg', '/audio/sfx/explosionCrunch_001.ogg', '/audio/sfx/explosionCrunch_002.ogg'],
  enemy: ['/audio/sfx/laserRetro_001.ogg'],
  enemyShot: ['/audio/sfx/laserLarge_001.ogg', '/audio/sfx/laserLarge_002.ogg'],
  base: ['/audio/sfx/lowFrequency_explosion_000.ogg', '/audio/sfx/lowFrequency_explosion_001.ogg'],
  upgrade: ['/audio/sfx/forceField_000.ogg', '/audio/sfx/forceField_001.ogg', '/audio/sfx/forceField_002.ogg'],
  level: ['/audio/sfx/computerNoise_000.ogg'],
  gameover: ['/audio/sfx/lowFrequency_explosion_001.ogg'],
  deny: ['/audio/sfx/computerNoise_001.ogg'],
  pickup: ['/audio/sfx/forceField_001.ogg'],
  shield: ['/audio/sfx/forceField_000.ogg', '/audio/sfx/forceField_002.ogg'],
  damage: ['/audio/sfx/impactMetal_001.ogg', '/audio/sfx/impactMetal_002.ogg'],
  ui: ['/audio/sfx/computerNoise_001.ogg'],
  engine: ['/audio/sfx/engineCircular_000.ogg', '/audio/sfx/engineCircular_001.ogg', '/audio/sfx/engineCircular_002.ogg'],
};

const SFX_VOLUME: Partial<Record<SoundKind, number>> = {
  shot: 0.34,
  flame: 0.28,
  rocket: 0.42,
  hit: 0.32,
  explode: 0.46,
  enemy: 0.24,
  enemyShot: 0.3,
  base: 0.48,
  upgrade: 0.3,
  level: 0.3,
  gameover: 0.44,
  deny: 0.24,
  pickup: 0.32,
  shield: 0.34,
  damage: 0.38,
  ui: 0.2,
  engine: 0.16,
};

export class AudioSystem {
  private context: AudioContext | null = null;
  private musicTimer: number | null = null;
  private musicStep = 0;
  private musicAudio: HTMLAudioElement | null = null;
  private engineAudio: HTMLAudioElement | null = null;
  private musicTrack: MusicTrackId = 'city';
  private musicVolume = 0.2;
  private soundVolume = 0.2;
  private musicPlaying = false;
  private engineRequested = false;
  private enabled = true;

  get isEnabled(): boolean { return this.enabled; }
  get currentMusicTrack(): MusicTrackId { return this.musicTrack; }
  get currentMusicVolume(): number { return this.musicVolume; }
  get currentSoundVolume(): number { return this.soundVolume; }

  setMusicVolume(volume: number): void {
    this.musicVolume = clampVolume(volume);
    if (this.musicAudio) this.musicAudio.volume = this.musicVolume;
  }

  setSoundVolume(volume: number): void {
    this.soundVolume = clampVolume(volume);
    if (this.engineAudio) this.engineAudio.volume = (SFX_VOLUME.engine ?? 0.16) * this.soundVolume;
  }

  toggle(): boolean {
    this.enabled = !this.enabled;
    if (this.enabled) {
      this.startMusic();
      if (this.engineRequested) this.startEngineAudio();
    } else {
      this.stopMusic();
      this.stopEngineAudio();
    }
    return this.enabled;
  }

  setMusicTrack(track: MusicTrackId): void {
    if (!MUSIC_TRACKS[track] || this.musicTrack === track) return;
    const wasPlaying = this.musicPlaying;
    this.stopMusic();
    this.musicTrack = track;
    if (wasPlaying) this.startMusic();
  }

  startMusic(): void {
    if (!this.enabled || this.musicPlaying) return;
    this.ensureContext();
    this.musicPlaying = true;
    this.musicStep = 0;
    this.stopMusicPlayback();
    const track = MUSIC_TRACKS[this.musicTrack];
    if (!track.src) {
      this.startSynthMusic();
      return;
    }

    const audio = this.createAudio(track.src, true, this.musicVolume);
    if (!audio) {
      this.startSynthMusic();
      return;
    }
    this.musicAudio = audio;
    const playback = audio.play();
    if (playback) {
      void playback.catch(() => {
        if (this.musicAudio !== audio || !this.musicPlaying) return;
        this.stopMusicPlayback();
        this.startSynthMusic();
      });
    }
  }

  stopMusic(): void {
    this.musicPlaying = false;
    this.stopMusicPlayback();
  }

  setEngineActive(active: boolean): void {
    this.engineRequested = active;
    if (!active || !this.enabled) {
      this.stopEngineAudio();
      return;
    }
    this.startEngineAudio();
  }

  play(kind: SoundKind): void {
    if (!this.enabled) return;
    this.ensureContext();
    const source = this.pickAsset(kind);
    if (source) {
      const audio = this.createAudio(source, false, (SFX_VOLUME[kind] ?? 0.3) * this.soundVolume);
      if (audio) {
        const playback = audio.play();
        if (playback) void playback.catch(() => this.playSynth(kind));
        return;
      }
    }
    this.playSynth(kind);
  }

  private readonly pickAsset = (kind: SoundKind): string | null => {
    const assets = SOUND_ASSETS[kind];
    if (!assets || assets.length === 0) return null;
    return assets[Math.floor(Math.random() * assets.length)] ?? null;
  };

  private readonly createAudio = (src: string, loop: boolean, volume: number): HTMLAudioElement | null => {
    if (typeof Audio === 'undefined') return null;
    const audio = new Audio(src);
    audio.preload = 'auto';
    audio.loop = loop;
    audio.volume = Math.max(0, Math.min(1, volume));
    return audio;
  };

  private readonly startEngineAudio = (): void => {
    if (!this.enabled || this.engineAudio) return;
    const source = this.pickAsset('engine');
    if (!source) return;
    const audio = this.createAudio(source, true, (SFX_VOLUME.engine ?? 0.16) * this.soundVolume);
    if (!audio) return;
    this.engineAudio = audio;
    const playback = audio.play();
    if (playback) void playback.catch(() => { if (this.engineAudio === audio) this.stopEngineAudio(); });
  };

  private readonly stopEngineAudio = (): void => {
    if (!this.engineAudio) return;
    this.engineAudio.pause();
    this.engineAudio.currentTime = 0;
    this.engineAudio = null;
  };

  private readonly stopMusicPlayback = (): void => {
    if (this.musicTimer !== null) {
      window.clearInterval(this.musicTimer);
      this.musicTimer = null;
    }
    if (this.musicAudio) {
      this.musicAudio.pause();
      this.musicAudio.currentTime = 0;
      this.musicAudio = null;
    }
  };

  private readonly startSynthMusic = (): void => {
    if (!this.context || this.musicTimer !== null || !this.enabled) return;
    const loop = (): void => {
      if (!this.context || !this.enabled || !this.musicPlaying) return;
      const time = this.context.currentTime + 0.02;
      const beat = this.musicStep % 16;
      if (this.musicTrack === 'electronic') {
        if (beat % 4 === 0) this.synth(55, time, 0.13, 'sine', 0.15 * this.musicVolume);
        if (beat % 4 === 2) this.synth(220, time, 0.06, 'square', 0.05 * this.musicVolume);
        const notes = [146.83, 164.81, 196, 220, 196, 164.81, 146.83, 130.81];
        this.synth(notes[this.musicStep % notes.length], time, 0.15, 'sawtooth', 0.055 * this.musicVolume, true);
      } else {
        if (beat % 8 === 0) this.synth(73.42, time, 0.22, 'triangle', 0.12 * this.musicVolume);
        if (beat % 4 === 2) this.synth(293.66, time, 0.09, 'square', 0.04 * this.musicVolume);
        const notes = [98, 116.54, 130.81, 146.83, 130.81, 116.54, 98, 87.31];
        this.synth(notes[this.musicStep % notes.length], time, 0.2, 'triangle', 0.045 * this.musicVolume, true);
      }
      this.musicStep++;
    };
    loop();
    this.musicTimer = window.setInterval(loop, 180);
  };

  private readonly playSynth = (kind: SoundKind): void => {
    if (!this.context) return;
    const [frequency, duration, type, volume] = SFX[kind];
    this.synth(frequency, this.context.currentTime + 0.01, duration, type, volume * this.soundVolume);
  };

  private ensureContext(): void {
    if (this.context) {
      if (this.context.state === 'suspended') void this.context.resume();
      return;
    }
    if (typeof window === 'undefined') return;
    const audioWindow = window as AudioWindow;
    const Context = window.AudioContext ?? audioWindow.webkitAudioContext;
    if (Context) this.context = new Context();
  }

  private synth(frequency: number, time: number, duration: number, type: OscillatorType, volume: number, filtered = false): void {
    if (!this.context || !this.enabled) return;
    const peakVolume = Math.max(0.0001, volume);
    const oscillator = this.context.createOscillator();
    const gain = this.context.createGain();
    oscillator.type = type;
    oscillator.frequency.setValueAtTime(frequency, time);
    if (filtered) {
      const filter = this.context.createBiquadFilter();
      filter.type = 'lowpass';
      filter.frequency.value = 740;
      oscillator.connect(filter);
      filter.connect(gain);
    } else {
      oscillator.connect(gain);
    }
    gain.connect(this.context.destination);
    gain.gain.setValueAtTime(0.0001, time);
    gain.gain.exponentialRampToValueAtTime(peakVolume, time + 0.008);
    gain.gain.exponentialRampToValueAtTime(0.0001, time + duration);
    oscillator.start(time);
    oscillator.stop(time + duration + 0.02);
  }
}

function clampVolume(value: number): number {
  return Number.isFinite(value) ? Math.max(0, Math.min(1, value)) : 0.2;
}
